import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";

const ThemeProviderContext = createContext({
    theme: "system",
    setTheme: () => null,
});

export function ThemeProvider({
    children,
    defaultTheme = "system",
    storageKey = "vite-ui-theme",
    user = null,
    onUserThemeChange,
}) {
    // Initialize theme from most reliable source
    const [theme, setTheme] = useState(() => {
        // Priority: localStorage > user.theme_preference > defaultTheme
        // This prevents sync issues during initialization
        const storedTheme = localStorage.getItem(storageKey);
        if (storedTheme) return storedTheme;
        
        if (user?.theme_preference) return user.theme_preference;
        
        return defaultTheme;
    });

    const [lastSavedTheme, setLastSavedTheme] = useState(null);
    
    // Track initialization state to prevent premature syncing
    const initializationRef = useRef({
        userFirstSeen: false,
        themeInitialized: false,
        isUserAction: false
    });

    // Debounce user data changes to prevent rapid re-syncing
    const userSyncTimeoutRef = useRef(null);

    // Stable reference for theme setting
    const setThemeStable = useCallback((newTheme, isUserAction = false) => {
        initializationRef.current.isUserAction = isUserAction;
        setTheme(newTheme);
        
        // Always update localStorage immediately for consistency
        localStorage.setItem(storageKey, newTheme);
    }, [storageKey]);

    // Handle user data synchronization with proper guards
    useEffect(() => {
        const { userFirstSeen, themeInitialized } = initializationRef.current;

        // Clear any pending sync
        if (userSyncTimeoutRef.current) {
            clearTimeout(userSyncTimeoutRef.current);
        }

        // Guard: Skip if no user data
        if (!user?.theme_preference) {
            if (user !== null) {
                initializationRef.current.userFirstSeen = true;
            }
            return;
        }

        // Guard: Skip if this is the first time seeing user data
        // (prevents overriding localStorage on initial load)
        if (!userFirstSeen) {
            initializationRef.current.userFirstSeen = true;
            initializationRef.current.themeInitialized = true;
            
            // Only sync if localStorage doesn't have a theme
            const storedTheme = localStorage.getItem(storageKey);
            if (!storedTheme) {
                setTheme(user.theme_preference);
            }
            return;
        }

        // Guard: Skip if theme hasn't been properly initialized
        if (!themeInitialized) {
            initializationRef.current.themeInitialized = true;
            return;
        }

        // Guard: Skip if user's theme matches current theme
        if (user.theme_preference === theme) {
            return;
        }

        // Guard: Skip if this was triggered by a user action
        if (initializationRef.current.isUserAction) {
            initializationRef.current.isUserAction = false;
            return;
        }

        // Debounced sync for legitimate user data changes
        userSyncTimeoutRef.current = setTimeout(() => {
            setTheme(user.theme_preference);
            localStorage.setItem(storageKey, user.theme_preference);
        }, 100); // 100ms debounce

    }, [user?.theme_preference, theme, storageKey]);

    // Apply theme to DOM
    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");

        if (theme === "system") {
            const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
                ? "dark"
                : "light";
            root.classList.add(systemTheme);
            return;
        }
        
        root.classList.add(theme);
    }, [theme]);

    // Handle system theme changes
    useEffect(() => {
        if (theme !== "system") return;

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = (e) => {
            const root = window.document.documentElement;
            root.classList.remove("light", "dark");
            root.classList.add(e.matches ? "dark" : "light");
        };

        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, [theme]);

    // Persist theme changes with proper error handling
    useEffect(() => {
        // Skip if theme hasn't actually changed
        if (theme === lastSavedTheme) {
            return;
        }

        // Skip if we don't have a stable user state
        if (user === null || user === undefined) {
            return;
        }

        // Handle unauthenticated users
        if (!user?.id) {
            setLastSavedTheme(theme);
            return;
        }

        // Optimistic update
        setLastSavedTheme(theme);

        // Persist to backend
        axios.post('/user/preferences', { theme_preference: theme })
            .then((response) => {
                if (onUserThemeChange) {
                    onUserThemeChange(theme);
                }
            })
            .catch((error) => {
                console.error('Theme: Database save failed:', error.response?.data || error.message);
                // The theme is already in localStorage, so this is not critical
                // Just notify the callback if it exists
                if (onUserThemeChange) {
                    onUserThemeChange(theme);
                }
            });

    }, [theme, user?.id, lastSavedTheme, onUserThemeChange]);

    // Cleanup timeouts
    useEffect(() => {
        return () => {
            if (userSyncTimeoutRef.current) {
                clearTimeout(userSyncTimeoutRef.current);
            }
        };
    }, []);

    // Public API that marks user actions
    const setThemeWithUserAction = useCallback((newTheme) => {
        console.log('Theme: User action changing to:', newTheme);
        setThemeStable(newTheme, true);
    }, [setThemeStable]);

    const value = {
        theme,
        setTheme: setThemeWithUserAction,
    };

    return (
        <ThemeProviderContext.Provider value={value}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

export const useTheme = () => {
    const context = useContext(ThemeProviderContext);
    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};